import React, { useEffect, useState } from 'react';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db, auth } from '../../services/firebase';
import { COLORS, LAYOUT } from '../../constants';

// Clock Icon
const ClockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

// Activity Icons
const ListingIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={COLORS.primary} strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <line x1="9" y1="9" x2="15" y2="9" />
    <line x1="9" y1="15" x2="15" y2="15" />
  </svg>
);

const PurchaseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9d0d1d" strokeWidth="2">
    <circle cx="9" cy="21" r="1" />
    <circle cx="20" cy="21" r="1" />
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
  </svg>
);

const SaleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#b01121" strokeWidth="2">
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const ProfileActivity = ({ userData }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRecentActivity = async () => {
      try {
        const userId = auth.currentUser?.uid;
        if (!userId) return;

        const recentActivities = [];

        // Fetch recent listings
        const listingsQuery = query(
          collection(db, 'listings'),
          where('sellerId', '==', userId),
          orderBy('createdAt', 'desc'),
          limit(3)
        );
        const listingsSnapshot = await getDocs(listingsQuery);
        listingsSnapshot.forEach((doc) => {
          const data = doc.data();
          recentActivities.push({
            id: doc.id,
            type: 'listing',
            title: `Listed: ${data.title}`,
            time: data.createdAt?.toDate?.() || new Date(),
            icon: <ListingIcon />
          });
        });

        // Fetch recent purchases
        const purchasesQuery = query(
          collection(db, 'transactions'),
          where('buyerId', '==', userId),
          orderBy('purchasedAt', 'desc'),
          limit(2)
        );
        const purchasesSnapshot = await getDocs(purchasesQuery);
        purchasesSnapshot.forEach((doc) => {
          const data = doc.data();
          recentActivities.push({
            id: doc.id,
            type: 'purchase',
            title: `Purchased item`,
            time: data.purchasedAt?.toDate?.() || new Date(),
            icon: <PurchaseIcon />
          });
        });

        // Fetch recent sales
        const salesQuery = query(
          collection(db, 'transactions'),
          where('sellerId', '==', userId),
          orderBy('purchasedAt', 'desc'),
          limit(2)
        );
        const salesSnapshot = await getDocs(salesQuery);
        salesSnapshot.forEach((doc) => {
          const data = doc.data();
          recentActivities.push({
            id: doc.id,
            type: 'sale',
            title: `Sold an item`,
            time: data.purchasedAt?.toDate?.() || new Date(),
            icon: <SaleIcon />
          });
        });

        // Sort by time and limit to 5
        recentActivities.sort((a, b) => b.time - a.time);
        setActivities(recentActivities.slice(0, 5));
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchRecentActivity();
  }, [userData]);

  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - date) / 1000);
    
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  const activityStyles = {
    container: {
      background: '#fff',
      borderRadius: 16,
      padding: LAYOUT.spacing.xl,
      boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)'
    },
    
    header: {
      fontSize: 20,
      fontWeight: 700,
      color: COLORS.primary,
      marginBottom: LAYOUT.spacing.lg
    },
    
    list: {
      display: 'flex',
      flexDirection: 'column',
      gap: LAYOUT.spacing.sm
    },
    
    item: {
      display: 'flex',
      alignItems: 'center',
      gap: LAYOUT.spacing.md,
      padding: LAYOUT.spacing.md,
      borderRadius: 12,
      background: '#f8f9fa',
      transition: 'all 0.2s ease'
    },
    
    iconWrapper: {
      width: 40,
      height: 40,
      borderRadius: 10,
      background: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
    },
    
    content: {
      flex: 1,
      minWidth: 0
    },
    
    title: {
      fontSize: 14,
      fontWeight: 600,
      color: COLORS.text.primary,
      marginBottom: 2,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    
    time: {
      fontSize: 12,
      color: COLORS.text.secondary,
      display: 'flex',
      alignItems: 'center',
      gap: 4
    },
    
    emptyState: {
      textAlign: 'center',
      padding: `${LAYOUT.spacing.xl}px 0`,
      color: COLORS.text.muted,
      fontSize: 14
    },
    
    loadingText: {
      textAlign: 'center',
      color: COLORS.text.secondary,
      padding: LAYOUT.spacing.lg
    }
  };

  if (loading) {
    return (
      <div style={activityStyles.container}>
        <div style={activityStyles.header}>Recent Activity</div>
        <div style={activityStyles.loadingText}>Loading activity...</div>
      </div>
    );
  }

  return (
    <div style={activityStyles.container}>
      <div style={activityStyles.header}>Recent Activity</div>
      {activities.length === 0 ? (
        <div style={activityStyles.emptyState}>
          No recent activity to display
        </div>
      ) : (
        <div style={activityStyles.list}>
          {activities.map((activity) => (
            <div
              key={activity.id}
              style={activityStyles.item}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#fff';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#f8f9fa';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={activityStyles.iconWrapper}>
                {activity.icon}
              </div>
              <div style={activityStyles.content}>
                <div style={activityStyles.title}>{activity.title}</div>
                <div style={activityStyles.time}>
                  <ClockIcon />
                  {getTimeAgo(activity.time)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProfileActivity;
